<template>
  <base-material-card color="primary" icon="mdi-card-multiple-outline" inline class="ml-0 px-5 py-4">
    <template v-slot:after-heading>
      <div class="display-2 font-weight-light">Módulos ANEEL - {{ titulo }}</div>
    </template>
    <v-btn
      id="btn-calcular-modulos"
      color="success"
      dark
      elevation="1"
      class="mt-5"
      style="float: left"
      relative
      text
      medium
      :loading="loadingBtn"
      @click="calcular()"
    >
      Calcular Módulos
    </v-btn>
    <v-text-field
      id="atualizacao-modulos-search"
      v-model="search"
      append-icon="mdi-magnify"
      class="break-search ml-auto mt-4"
      label="Procurar"
      hide-details
      single-line
      style="max-width: 250px"
    />
    <v-alert dense outlined type="info" color="blue" class="mb-3 mt-7 px-1 py-2">
      Uma vez por dia, os módulos são calculados com base nos
      <a id="link-modulos" @click="$router.push({ path: '/atualizacao-indices/index' })">Índices IPCA</a>
      cadastrados na base do sistema.
    </v-alert>
    <general-progress-bars class="mt-11" :items="downloadProgressData" prefixText="Baixando Modulos Aneel" />
    <v-divider class="mt-10" />
    <v-data-table
      :headers="headers"
      :items="items"
      :search.sync="search"
      :loading="loading"
      :headerProps="{
        sortByText: 'Ordenar Por'
      }"
      :footer-props="{
        itemsPerPageText: 'Itens por Página'
      }"
      :mobile-breakpoint="800"
      loading-text="Carregando..."
      no-data-text="Nenhum modulo encontrado"
      no-results-text="Nenhum registro correspondente encontrado"
    >
      <template v-slot:item="row">
        <v-tooltip bottom :disabled="row.item.IndiceOficial == 'Sim'">
          <template v-slot:activator="{ on, attrs }">
            <tr :class="row.item.IndiceOficial == 'Não' ? 'atualizacao-modulos-table-tr' : ''" v-bind="attrs" v-on="on">
              <td class="text-start">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on: onDataRegistro, attrs: attrsDataRegistro }">
                    <span
                      id="link-visualizar-modulo"
                      v-bind="attrsDataRegistro"
                      v-on="onDataRegistro"
                      @click="$router.push({ path: `visualizar/${row.item.DataRegistro.substring(0, 10)}/${versao}` })"
                    >
                      {{ row.item.DataRegistro | formatToMonth }}
                    </span>
                  </template>
                  <span>Clique aqui para visualizar o Módulo ANEEL</span>
                </v-tooltip>
              </td>
              <td class="text-center">
                <v-btn
                  id="action-baixar-csv-modulos"
                  class="px-1"
                  min-width="0"
                  fab
                  icon
                  x-small
                  @click="download(versao, row.item.DataRegistro)"
                >
                  <v-icon small>mdi-download</v-icon>
                </v-btn>
                <template v-if="ultimoRegistro == row.item.DataRegistro">
                  <v-btn
                    id="action-excluir-modulo"
                    class="px-1"
                    min-width="0"
                    fab
                    icon
                    x-small
                    :loading="loadingDelete"
                    @click="openDialogDelete(row.item.DataRegistro)"
                  >
                    <v-icon small>mdi-trash-can-outline</v-icon>
                  </v-btn>
                </template>
              </td>
            </tr>
          </template>
          <span>Esse Módulo foi cálculado utilizado um IPCA estimado, ou seja, não oficial</span>
        </v-tooltip>
      </template>
      <template v-slot:[`footer.page-text`]>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon color="primary" v-bind="attrs" v-on="on" @click="$emit('rechargeTable')">mdi-refresh</v-icon>
          </template>
          <span>Clique aqui para recarregar os módulos</span>
        </v-tooltip>
      </template>
    </v-data-table>
    <div v-if="versao == 'Atual'">
      <v-btn
        id="btn-visualizar-modulos-anteriores"
        class="mb-4"
        color="success"
        elevation="1"
        text
        @click="$emit('changeVersao', '2016')"
      >
        <v-icon left>mdi-eye</v-icon>
        MÓDULOS ANTERIORES
      </v-btn>
    </div>
    <div v-else>
      <v-btn class="mb-4" color="success" elevation="1" text @click="$emit('changeVersao', 'Atual')">
        <v-icon left>mdi-eye</v-icon>
        MÓDULOS ATUAIS
      </v-btn>
    </div>
    <dialog-delete
      :showDialog="dialogDelete"
      @changeValue="dialogDelete = $event"
      @deleteTriggered="deletarItem(dialogDeleteData.id, versao)"
    >
      <template slot="title">Deletar Modulos Aneel #{{ dialogDeleteData.id | formatToMonth }}</template>
      <template slot="body">
        Tem certeza que deseja deletar os Modulos Aneel do mês
        <strong>{{ dialogDeleteData.id | formatToMonth }}</strong>
        ?
      </template>
    </dialog-delete>
  </base-material-card>
</template>

<script>
  import ModulosAneelService from '@/services/ModulosAneelService';
  import powerupsActionsMixins from '@/mixins/powerupsActionsMixins';
  import dayjs from 'dayjs';

  export default {
    components: {
      GeneralProgressBars: () => import('@/components/general/GeneralProgressBars.vue'),
      DialogDelete: () => import('@/components/general/DialogDelete')
    },
    mixins: [powerupsActionsMixins],

    props: {
      items: {
        type: Array,
        default: () => []
      },
      loading: {
        type: Boolean,
        required: true
      },
      ultimoRegistro: {
        type: String,
        default: () => ''
      },
      versao: {
        type: String,
        default: () => ''
      }
    },

    emits: ['rechargeTable', 'changeVersao'],

    data: () => ({
      powerupService: ModulosAneelService,
      powerupEntityName: 'Atualização de Modulos ANEEL',
      search: '',
      loadingBtn: false,
      loadingDelete: false,
      headers: [
        {
          text: 'Data do Registro',
          value: 'DataRegistro'
        },
        {
          sortable: false,
          text: 'Ações',
          value: 'actions',
          align: 'center'
        }
      ]
    }),

    computed: {
      titulo() {
        return this.versao == '2016' ? 'Versão Anterior (Data < 08/2021)' : 'Versão Atual (Data >= 08/2021)';
      }
    },

    methods: {
      deletarItem(data, versao) {
        if (this.loadingDelete) return false;

        this.loadingDelete = true;
        this.dialogDelete = false;

        this.powerupService
          .deletar(data, versao)
          .then(() => {
            this.$toast.success(`Modulos Aneel removido com sucesso.`, '', { position: 'topRight' });
            this.$emit('rechargeTable');
          })
          .catch(() => {
            this.$toast.error(`Erro ao deletar Modulo.`, '', { position: 'topRight' });
          })
          .finally(() => (this.loadingDelete = false));
      },

      download(versao, date) {
        var data = { data: date, versao: versao };
        const nomeArquivo = `Modulos Aneel versão ${versao} - ${dayjs(date).format('MM-YYYY')}.csv`;

        this.$set(this.downloadProgressData, data, { date });
        this.$toast.info('Preparando seu arquivo para download.', '', { position: 'topRight', timeout: 2000 });

        ModulosAneelService.download(data, {
          timeout: 60 * 60 * 1000,
          onDownloadProgress: (progressEvent) => {
            let progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            let isComputable = progressEvent.lengthComputable;
            this.$set(this.downloadProgressData, data, { progress, isComputable, date });
          }
        })
          .then((res) => {
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', nomeArquivo);
            document.body.appendChild(link);
            link.click();
          })
          .catch((err) => {
            this.$toast.error('Falha no download do Modulo.', '', { position: 'topRight' });
            console.error('Error: ', err);
          })
          .finally(() => {
            this.$delete(this.downloadProgressData, data);
          });
      },

      calcular() {
        this.loadingBtn = true;
        ModulosAneelService.calcular()
          .catch(() => {
            this.$toast.error(`Erro ao calcular Modulo.`, '', { position: 'topRight' });
          })
          .finally(() => {
            this.$emit('rechargeTable');
            this.$toast.info(`Calculando Modulos Aneel`, '', { position: 'topRight' });
            this.loadingBtn = false;
          });
      }
    }
  };
</script>

<style>
  #link-visualizar-modulo {
    cursor: pointer;
    color: #e5502c;
    text-decoration: underline;
  }

  #link-modulos {
    color: #e5502c;
    font-weight: bold;
    text-decoration: underline;
  }

  .atualizacao-modulos-table-tr {
    /* background-color: #b3ccdd; */
    background-color: #ffc9c9;
  }
</style>
